import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Box, Container } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { RootState } from './app/store';
import Footer from './components/Footer';
import InProgressBar from './components/InProgressBar';
import Loading from './components/Loading';
import Menu from './components/Menu';
import Navbar from './components/Navbar';
import NormalRoute from './components/NormalRoute';
import PrivateRoute from './components/PrivateRoute';
import { DRAWER_WIDTH } from './constants';
import { useAppDispatch } from './hook/useAppRudux';
import AlertBox from './pages/AlertBox';
import {
  autoSignInRedirect,
  forceSignOut,
  getActiveAccount,
  getToken,
  redirectAfterLogin,
  signOut
} from './pages/Auth/authSCG';
import ChangePassword from './pages/ChangePassword';
import Error403 from './pages/Error/403';
import Error404 from './pages/Error/404';
import Home from './pages/Home';
import loadingSlice from './pages/Loading/loadingSlice';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Service from './pages/Service';
import { verifyScgUserAsync } from './pages/UserProfile/userProfileSlice';

let isForceSignOut = false;

export default function App() {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const token = getToken();
  const activeAccountKey = getActiveAccount();
  const { instance, inProgress, accounts } = useMsal();
  const { exp, user } = useSelector((state: RootState) => state.userProfile);
  const isExternalUser = Boolean(user?.isExternalUser);
  const { isLoading } = useSelector((state: RootState) => state.loading);

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const accessTokenRequest = {
    scopes: ['user.read'],
    account: accounts[0]
  };
  const isLoggedIn = Boolean(user);
  const isExpired = exp ? exp < Date.now() / 1000 : false;

  useEffect(() => {
    // Get Access token from SCG Azure
    async function authenticate() {
      try {
        await dispatch(
          verifyScgUserAsync({ accessTokenRequest, instance })
        ).unwrap();
        // Clear error that came from token not found and error information still stored in the state
        dispatch(loadingSlice.actions.clear());
        redirectAfterLogin(history);
        isForceSignOut = false;
      } catch (error) {
        if (error instanceof Error)
          loadingSlice.actions.error({ message: error.message });
        setTimeout(() => signOut({ dispatch, instance }), 1000); // Delay timer for displaying the error
      }
    }

    // Checking conditions before logging in with SCG Azure
    if (!token && inProgress === InteractionStatus.None) {
      if (accessTokenRequest.account) {
        authenticate();
      } else if (activeAccountKey) {
        autoSignInRedirect(instance, activeAccountKey);
      }
    }
  }, [accounts, inProgress, isForceSignOut]);

  function handleDrawer(open: boolean) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setOpenDrawer(open);
    };
  }

  if (isExpired) {
    forceSignOut(dispatch);
    isForceSignOut = true; // To call useEffect for re-authorizing
  }

  // Use for preventing render app
  if (!token && inProgress !== InteractionStatus.None) {
    let text = '';

    switch (inProgress) {
      case 'login':
        text = 'Taking you to the login page...';
        break;
      case 'handleRedirect':
        text = 'Retrieving data...';
        break;
      case 'logout':
        text = 'Taking you to log out page...';
        break;
      default:
        text = 'Loading...';
        break;
    }

    return <Loading text={text} />;
  }

  if (!token && accessTokenRequest.account) {
    return (
      <>
        {/* To display error if cannot find the user in backspace's database, hide when system force sign out */}
        {!isForceSignOut && <AlertWithInformation />}
        <Loading text="Retrieving user data..." />
      </>
    );
  }

  return (
    <div>
      <AlertWithInformation />
      {isLoading && <InProgressBar />}
      <Box sx={{ ml: { lg: isLoggedIn ? `${DRAWER_WIDTH}px` : 0 } }}>
        <Navbar handleDrawer={handleDrawer} isLoggedIn={isLoggedIn} />
        {isLoggedIn && (
          <Menu openDrawer={openDrawer} onCloseDrawer={handleDrawer(false)} />
        )}
        <Container
          id="container"
          sx={{
            mt: { xs: 8.5, sm: 9.5 },
            minHeight: {
              xs: 'calc(100vh - 68px - 148px)', // 8.5 * 8px = 68px + Footer
              sm: 'calc(100vh - 76px - 156px)' // 9.5 * 8px = 76px + Footer
            }
          }}
        >
          <Switch>
            <Route exact path="/" component={Home} />
            <Route exact path="/home" component={() => <Redirect to="/" />} />
            <Route path="/services" component={Service} />
            <Route
              exact
              path="/account"
              component={() => <Redirect to="/" />}
            />
            <NormalRoute exact path="/login" component={Login} />
            <Route path="/reset-passwords" component={ResetPassword} />
            <PrivateRoute
              path="/change-passwords"
              component={
                isExternalUser ? ChangePassword : () => <Redirect to="/" />
              }
            />
            <Route exact path="/error/403" component={Error403} />
            <Route exact path="/error/404" component={Error404} />
            <Route
              exact
              path="*"
              component={() => <Redirect to="/error/404" />}
            />
          </Switch>
        </Container>
        <Footer />
      </Box>
    </div>
  );
}

function AlertWithInformation(): JSX.Element {
  const dispatch = useAppDispatch();
  const { openAlert, duration, status, message } = useSelector(
    (state: RootState) => state.loading
  );

  return (
    <AlertBox
      open={openAlert}
      onClose={() => dispatch(loadingSlice.actions.clear())}
      autoHideDuration={duration}
      severity={status}
    >
      {message}
    </AlertBox>
  );
}
